import { CloseHealth } from "@common/types/closeHealth";
import { CloseStatus } from "@common/types/closeStatus";
import {
    ClientsWorkOverviewColumn,
    ClientsWorkOverviewSortValue,
    ClientsWorkOverviewView,
} from "@common/types/views/clientsWorkOverviewView";
import { NamedView } from "@common/types/views/views";
import type { WorkCardFieldId } from "../board/workCardFieldColumns";
import { buildWorkView } from "./buildWorkView";
import {
    ACTIVE_SERVICES_VIEW_ID,
    ALL_WORK_VIEW_ID,
    ASSIGNED_TO_ME_WORK_VIEW_ID,
    BY_TEAM_MEMBER_VIEW_ID,
    MONTH_END_CLOSE_VIEW_ID,
    NEEDS_ATTENTION_VIEW_ID,
    UPCOMING_DEADLINES_VIEW_ID,
} from "./workViewIds";

const {
    WORKSPACE_NAME,
    ENTITY,
    SERVICE_PERIOD,
    CLOSE_STAGE,
    HEALTH,
    MY_ROLE,
    WORK_DELIVERY_DATE,
    TIME_IN_STAGE,
    PREPARER_REVIEWER,
    TASK_PROGRESS,
    ESTIMATED_TIME,
    APPLICATION_TYPE,
    UNREAD_TASKS,
} = ClientsWorkOverviewColumn;

/** All work: today's columns, no filters, Applied at newest first. */
export const ALL_WORK_VIEW = buildWorkView({
    id: ALL_WORK_VIEW_ID,
    name: "All work",
    columnIds: [
        WORKSPACE_NAME,
        ENTITY,
        SERVICE_PERIOD,
        CLOSE_STAGE,
        TIME_IN_STAGE,
        PREPARER_REVIEWER,
        TASK_PROGRESS,
        ESTIMATED_TIME,
        APPLICATION_TYPE,
        UNREAD_TASKS,
    ],
    filters: {},
    groupBy: "none",
});

/** Client, Entity, Service period and Stage open every Kick view after Service. */
const STAGE_COLUMN_IDS = [WORKSPACE_NAME, ENTITY, SERVICE_PERIOD, CLOSE_STAGE];

/** Days in stage follows Stage everywhere except Needs attention, which leads with Health. */
const LEADING_COLUMN_IDS = [...STAGE_COLUMN_IDS, TIME_IN_STAGE];

/** Month-end close's card tags, which Assigned to me shares. */
const DAYS_IN_STAGE_CARD_FIELDS: WorkCardFieldId[] = [
    TIME_IN_STAGE,
    TASK_PROGRESS,
];

const OPEN_WORK_FILTERS = {
    closeStatuses: [CloseStatus.OPEN],
    workSort: ClientsWorkOverviewSortValue.WORK_DELIVERY_DATE_ASC,
} satisfies ClientsWorkOverviewView["filters"];

const MONTH_END_CLOSE_FILTERS = {
    ...OPEN_WORK_FILTERS,
    closeStatuses: [CloseStatus.OPEN, CloseStatus.DONE],
    workDeliveryDatePreset: "this_month",
} satisfies ClientsWorkOverviewView["filters"];

const ACTIVE_SERVICES_VIEW = buildWorkView({
    id: ACTIVE_SERVICES_VIEW_ID,
    name: "Active services",
    columnIds: [
        ...LEADING_COLUMN_IDS,
        WORK_DELIVERY_DATE,
        PREPARER_REVIEWER,
        TASK_PROGRESS,
        UNREAD_TASKS,
    ],
    filters: OPEN_WORK_FILTERS,
    groupBy: "none",
    board: { cardFields: [WORK_DELIVERY_DATE, TASK_PROGRESS] },
});

const MONTH_END_CLOSE_VIEW = buildWorkView({
    id: MONTH_END_CLOSE_VIEW_ID,
    name: "Month-end close",
    columnIds: [
        ...LEADING_COLUMN_IDS,
        WORK_DELIVERY_DATE,
        PREPARER_REVIEWER,
        TASK_PROGRESS,
    ],
    filters: MONTH_END_CLOSE_FILTERS,
    groupBy: "none",
    board: { cardFields: DAYS_IN_STAGE_CARD_FIELDS },
});

/** Month-end close narrowed to work where the user is on the engagement team in any role. */
const buildAssignedToMeView = (userId: number) =>
    buildWorkView({
        id: ASSIGNED_TO_ME_WORK_VIEW_ID,
        name: "Assigned to me",
        columnIds: [
            ...LEADING_COLUMN_IDS,
            WORK_DELIVERY_DATE,
            MY_ROLE,
            TASK_PROGRESS,
        ],
        filters: {
            ...MONTH_END_CLOSE_FILTERS,
            engagementTeamMemberIds: [userId],
        },
        groupBy: "none",
        board: { cardFields: DAYS_IN_STAGE_CARD_FIELDS },
    });

const UPCOMING_DEADLINES_VIEW = buildWorkView({
    id: UPCOMING_DEADLINES_VIEW_ID,
    name: "Upcoming deadlines",
    columnIds: [
        ...LEADING_COLUMN_IDS,
        WORK_DELIVERY_DATE,
        PREPARER_REVIEWER,
        TASK_PROGRESS,
    ],
    filters: { ...OPEN_WORK_FILTERS, workDeliveryDatePreset: "next_14_days" },
    groupBy: "due_date",
    board: {
        isEmptyColumnsShown: false,
        cardFields: [WORK_DELIVERY_DATE, CLOSE_STAGE, TASK_PROGRESS],
    },
});

const NEEDS_ATTENTION_VIEW = buildWorkView({
    id: NEEDS_ATTENTION_VIEW_ID,
    name: "Needs attention",
    columnIds: [
        ...STAGE_COLUMN_IDS,
        HEALTH,
        WORK_DELIVERY_DATE,
        TIME_IN_STAGE,
        PREPARER_REVIEWER,
        TASK_PROGRESS,
    ],
    filters: {
        closeStatuses: [CloseStatus.OPEN],
        healthStatuses: [CloseHealth.AT_RISK, CloseHealth.WATCHING],
        workSort: ClientsWorkOverviewSortValue.HEALTH_ASC,
    },
    groupBy: "none",
    board: {
        isEmptyColumnsShown: false,
        cardFields: [WORK_DELIVERY_DATE, TIME_IN_STAGE, TASK_PROGRESS],
    },
});

/** Grouped by preparer, so Board shows stage columns with preparer lanes, "No preparer" last. */
const BY_TEAM_MEMBER_VIEW = buildWorkView({
    id: BY_TEAM_MEMBER_VIEW_ID,
    name: "By team member",
    columnIds: [
        ...LEADING_COLUMN_IDS,
        WORK_DELIVERY_DATE,
        TASK_PROGRESS,
        ESTIMATED_TIME,
    ],
    filters: OPEN_WORK_FILTERS,
    groupBy: "preparer",
    board: { cardFields: [MY_ROLE, CLOSE_STAGE, TASK_PROGRESS] },
});

/**
 * The Kick views in picker order. Assigned to me needs the signed-in
 * user's id and is left out until it's known.
 */
export const buildKickWorkViews = ({
    userId,
}: {
    userId: number | undefined;
}): Array<NamedView<ClientsWorkOverviewView>> => [
    ACTIVE_SERVICES_VIEW,
    MONTH_END_CLOSE_VIEW,
    ...(userId === undefined ? [] : [buildAssignedToMeView(userId)]),
    UPCOMING_DEADLINES_VIEW,
    NEEDS_ATTENTION_VIEW,
    BY_TEAM_MEMBER_VIEW,
    ALL_WORK_VIEW,
];
